import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * Format a date string into human-readable format
 * @param dateString ISO date string to format
 * @param formatString Optional format string for date-fns
 * @returns Formatted date string
 */
export function formatDate(dateString?: string, formatString: string = 'MMM d, yyyy'): string {
  if (!dateString) return 'N/A';
  
  try {
    const date = parseISO(dateString);
    
    if (!isValid(date)) {
      return 'Invalid date';
    }
    
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Format a date string as relative time (e.g. "2 days ago")
 * @param dateString ISO date string to format
 * @returns Relative time string
 */
export function formatRelativeTime(dateString?: string): string {
  if (!dateString) return 'N/A';
  
  try {
    const date = parseISO(dateString);
    
    if (!isValid(date)) {
      return 'Invalid date';
    }
    
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid date';
  }
}

/**
 * Format a date string for datetime input fields (YYYY-MM-DD)
 * @param dateString ISO date string to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForInput(dateString?: string): string {
  if (!dateString) return '';
  
  try {
    const date = parseISO(dateString);
    
    if (!isValid(date)) {
      return '';
    }
    
    return format(date, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
}

/**
 * Check if a date is in the past
 * @param dateString ISO date string to check
 * @returns Boolean indicating if the date is in the past
 */
export function isPastDate(dateString?: string): boolean {
  if (!dateString) return false;
  
  try {
    const date = parseISO(dateString);
    return date < new Date();
  } catch (error) {
    console.error('Error checking if date is in past:', error);
    return false;
  }
}

/**
 * Format a date with time
 * @param dateString ISO date string to format
 * @returns Formatted date string with time
 */
export function formatDateTime(dateString?: string): string {
  if (!dateString) return 'N/A';
  
  try {
    const date = parseISO(dateString);
    
    if (!isValid(date)) {
      return 'Invalid date';
    }
    
    return format(date, 'MMM d, yyyy h:mm a');
  } catch (error) {
    console.error('Error formatting date and time:', error);
    return 'Invalid date';
  }
}